// packages/console/src/components/panels/LiveAggregatePanel.tsx
//
// Aggregate-output panel for the LIVE console, ported from the "Aggregate
// output" block of docs/design/ui_kits/aether-live/Transcoder.jsx. It reads the
// REAL LiveStream sample: egress bitrate, glass-to-glass latency, frames encoded
// and dropped, over a 60-sample throughput Graph.
//
// The sparkline is a ring buffer the parent LiveTranscoder holds and feeds at
// the stream's own cadence (never a random walk). Honest absence (R10): a
// stopped or unknown figure renders an em-dash, and the graph line idles when
// the session is not on air.

import { Panel, Read, Graph } from "../Parts";
import type { LiveStream } from "../../api/live";
import { EM_DASH, fmtInt, fmtMbps, fmtNum, mbps } from "../../lib/format";

export function LiveAggregatePanel({
  stream,
  hist,
  onAir,
  activeStreams,
}: {
  stream: LiveStream | null;
  hist: number[];
  onAir: boolean;
  activeStreams: number | null;
}) {
  const egressMbps = mbps(stream?.egressBitsPerSec ?? null);
  // The graph max scales to the current egress with headroom, floored so an idle
  // stream still shows a sensible grid.
  const graphMax = Math.max(44, (egressMbps ?? 0) * 1.3);
  const dropped = stream?.framesDropped ?? null;
  return (
    <Panel
      title="Aggregate output"
      style={{ flex: 1, minWidth: 0 }}
      right={
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--fg3)" }}>
          {activeStreams === null ? EM_DASH : `${activeStreams} streams`}
        </span>
      }
    >
      <div style={{ display: "flex", gap: 22, marginBottom: 11 }}>
        <Read value={fmtMbps(stream?.egressBitsPerSec ?? null)} unit="Mb/s" label="Egress" size={22} />
        <Read value={fmtNum(stream?.glassToGlassMs ?? null, 0)} unit="ms" label="Glass to glass" size={22} />
        <Read value={fmtInt(stream?.framesEncoded ?? null)} label="Frames encoded" size={22} />
        <Read
          value={fmtInt(dropped)}
          label="Dropped"
          size={22}
          tone={dropped !== null ? (dropped === 0 ? "var(--ok)" : "var(--warn)") : undefined}
        />
      </div>
      <Graph data={hist} max={graphMax} live={onAir} />
    </Panel>
  );
}
